import styled from 'styled-components';
import { useRouter } from 'next/router';
import { EntityTypes } from '../../../../new-lib/constants/common';
import { CardClickEventData } from '../../../../new-lib/types/common';
import Link from 'next/link';
import ImageWrapper from '../../../../new-components/ImageWrapper';
import { useSelector } from 'react-redux';
import { handleCardClickEvent } from '../../../../helpers/cards';
import { getImagePath } from '../../../../helpers/common';
import CardTags from '../../../../new-components/cards/CardTags';
import CardCompareBtn from '../../../../new-components/cards/components/CardCompareBtn';
import CardFavBtn from '../../../../new-components/cards/components/CardFavBtn';
import ImageInfoSection from '../../../../new-components/cards/components/ImageInfoSection';
import CardTitleSection from '../../../../new-components/cards/components/CardTitleSection';
import PropertyDetails from '../../../../new-components/cards/components/PropertyDetails';
import PriceDetails from '../../../../new-components/cards/components/PriceDetails';
import CardCTA from '../../../../new-components/cards/components/CardCTA';
import { useTranslation } from 'next-i18next';
import { RecommendedProperty } from '../../dtos';
import CardShareIcon from '../../../../new-components/cards/components/CardShareBtn';
import { isMobile } from 'react-device-detect';

let HOST = process.env.NEXT_PUBLIC_HOST;

interface CardProps {
  entity: RecommendedProperty;
  clickEventData: CardClickEventData;
  entityType: EntityTypes;
  cardPosition: number | undefined;
  recommendedClick?: boolean;
  isNawyNow?: boolean;
}

const RecommendedPropertyCard = ({
  entity,
  clickEventData,
  entityType,
  cardPosition,
  recommendedClick,
  isNawyNow
}: CardProps): JSX.Element => {
  const { t } = useTranslation('common');
  const isMobile = useSelector((state: any) => state.app.isMobile);
  const favorites = useSelector(
    (state: any) => state.favorites[`${entityType}Favorites`]
  );
  const checkIsFavorite = () => {
    return favorites.some((id: any) => id === entity.id);
  };

  const { locale } = useRouter();
  const formatDate = (inputDate: string) => {
    const date = new Date(inputDate);

    const year = date.getFullYear() + 1;
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  };

  return (
    <Link
      href={{
        pathname: `/compound/${entity.compoundSlug}/property/${entity?.path}`,
        query: { ...(recommendedClick && { recommended: true }) }
      }}
      style={{ width: '100%' }}
      onClick={() => handleCardClickEvent(clickEventData)}
    >
      <Container>
        <UpperSection isNawyNow>
          <ImageWrapper
            src={getImagePath(entity.coverImageUrl)}
            fill
            className="card-image"
            objectFit="cover"
            alt={entity.path}
            dataTest="card-image"
            imageSize={isMobile ? 'low' : 'high'}
            quality={50}
            sizes="70vw"
            withSkeleton
            loading={'lazy'}
          />
          {!isMobile && entity.isLaunchSummary ? (
            <CardTags tags={[...(entity.isLaunchSummary ? ['Launch'] : [])]} />
          ) : (
            <></>
          )}
          <div className="inner-content">
            <div className="card-action-btns">
              <CardCompareBtn
                clickEventData={clickEventData}
                entityData={{ id: entity.id, min_price: entity.totalPrice }}
                entityType={entityType}
                showToolTip={cardPosition === 0}
              />
              <CardShareIcon
                clickEventData={clickEventData}
                entityData={{ id: entity.id, min_price: entity.totalPrice }}
                cardPosition={cardPosition}
                shareUrl={`${HOST}/${locale === 'ar' ? 'ar/' : ''}compound/${
                  entity?.compoundSlug
                }/property/${entity?.path}`}
              />
              <CardFavBtn
                clickEventData={clickEventData}
                entity={entity}
                entityType={entityType}
                isFavorite={checkIsFavorite()}
              />
            </div>
            <ImageInfoSection
              entityType={entityType}
              entityData={{
                has_launches: false,
                has_offer: false,
                property_type: { name: entity?.propertyType },
                tags: [],
                logo_path: entity.developerLogoUrl,
                isNawyNow: isNawyNow,
                min_ready_by: formatDate(entity.deliveryIn)
              }}
            />
          </div>
        </UpperSection>
        <LowerSection isNawyNow={isNawyNow}>
          <CardTitleSection
            entityData={{
              compoundName: entity?.compoundName,
              propertyType: { name: entity.propertyType },
              area: `${entity?.areaName}, ${t('egypt')}`,
              logo_path: entity.developerLogoUrl,
              has_offer: false,
              isNawyNow
            }}
            entityType={entityType}
          />

          <PropertyDetails
            entityData={{
              min_ready_by: formatDate(entity.deliveryIn),
              min_unit_area: entity.unitArea,
              number_of_bathrooms: entity.numberOfBathrooms,
              number_of_bedrooms: entity.numberOfBedrooms,
              isNawyNow: isNawyNow
            }}
          />

          <div className="price-cta-section">
            <PriceDetails
              entityType={entityType}
              entityData={{
                installment_type: entity.installmentType,
                max_installment_years: entity.installmentYears ?? 0,
                max_installment_years_months: 0,
                min_down_payment: 0,
                min_installments: entity.monthlyInstallment,
                min_price: entity.totalPrice,
                currency: entity.currency,
                is_cash: entity.isCash,
                down_payment_percentage: entity.downPaymentPercentage,
                isNawyNow
              }}
            />

            <CardCTA
              clickEventData={clickEventData}
              entity={entity}
              entityType={entityType}
            />
          </div>
        </LowerSection>
      </Container>
    </Link>
  );
};

const Container = styled.div<any>`
  width: 100%;
  height: 100%;
  border-radius: ${props => props.theme.borderRadius};
  overflow: hidden;
  border: 1px solid #e6e6e6;
  cursor: pointer;
  .developer-img {
    width: 45px;
    height: 45px;
    background-color: #ffffff;
    border-radius: 50%;
    position: relative;
    overflow: hidden;
  }
`;

const UpperSection = styled.div<any>`
  position: relative;
  width: 100%;
  height: ${props => (props.isNawyNow ? '216px' : '235px')};
  overflow: hidden;

  .inner-content {
    position: absolute;
    width: 100%;
    height: 100%;
    padding: 0px 16px 32px 16px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    background: linear-gradient(360deg, #000000 -25%, rgba(0, 0, 0, 0) 50%);
    .card-action-btns {
      display: flex;
      flex-direction: row;
      gap: 4px;
      justify-content: flex-end;
      margin-top: 10px;
    }
    .img-lower-section {
      width: 100;
    }
  }
`;

const LowerSection = styled.div<any>`
  padding: 16px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background-color: ${props =>
    props.isNawyNow && props.theme.light.colors.secondaryBlue};
  .price-cta-section {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-end;
    height: 60px;

    .payment-details {
      height: 38px;
      text-align: start;
    }
  }
`;

export default RecommendedPropertyCard;
